import React from 'react';
import Header from '../../../components/layout/header/landing';
import useVendingPickingDashboard from './hook';

function formatMinutesToHHMM(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

// Status is numeric from backend; map to badge style by code.
function getVMStatusBadgeClasses(status: number | string): string {
  const s = String(status);
  // adjust mapping as desired
  if (s === '1') return 'badge bg-primary-subtle text-primary'; // picking / in progress
  if (s === '3') return 'badge bg-success-subtle text-success'; // picked / completed
  if (s === '2') return 'badge bg-warning-subtle text-warning'; // partially picked
  return 'badge bg-secondary-subtle text-secondary'; // ready / other
}

// Currently unused but kept in case you add shorted item status later
function getShortedStatusClasses(status: string): string {
  if (status === 'Unresolved') return 'badge bg-danger-subtle text-danger';
  return 'badge bg-secondary-subtle text-secondary';
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .map((n) => n[0])
    .join('')
    .toUpperCase();
}

function getPickerDisplayName(
  pickerName?: string | null,
  first?: string | null,
  last?: string | null,
) {
  if (pickerName && pickerName.trim().length > 0) return pickerName;
  const full = `${first || ''} ${last || ''}`.trim();
  return full || 'Unknown';
}

function getDriverDisplayName(
  driverName?: string | null,
  first?: string | null,
  last?: string | null,
) {
  if (driverName && driverName.trim().length > 0) return driverName;
  const full = `${first || ''} ${last || ''}`.trim();
  return full || 'Unknown';
}

function getStatusLabel(status: number | string): string {
  const s = Number(status);
  if (s === 1) return 'Picking';
  if (s === 5) return 'Finished Picking';
  return `Status ${s}`;
}

function getStatusBadgeClasses(status: number | string): string {
  const s = Number(status);
  if (s === 1) return 'badge bg-primary-subtle text-primary'; // Picking
  if (s === 5) return 'badge bg-success-subtle text-success'; // Finished picking
  return 'badge bg-secondary-subtle text-secondary';
}

function VendingPickingDashboard(): React.ReactElement {
  const {
    currentPicking,
    routes,
    shortedItems,
    pickerStats,
    selectedPickerId,
    setSelectedPickerId,
  } = useVendingPickingDashboard();

  // Safe derived values for progress bar
  const itemsPicked = currentPicking?.itemsPicked ?? 0;
  const totalItems = currentPicking?.totalItems ?? 0;
  const progressPct = totalItems > 0 ? (itemsPicked / totalItems) * 100 : 0;

  return (
    <div className="py-4">
      <Header title="Picking Progress" />
      <div className="px-4 px-xl-5">
        {/* Filters row */}
        <div className="d-flex flex-wrap align-items-end mb-4 gap-3">
          <div>
            <div className="fz-18 fw-600 mb-1">Vending Machine Picking</div>
            <div className="fz-12 text-muted">
              Live picking status, shorted items, and picker performance.
            </div>
          </div>

          <div className="ms-auto d-flex flex-wrap gap-3">
            {/* Date range */}
            <div>
              <label
                className="fz-12 text-muted d-block"
                htmlFor="picking-date-range"
              >
                <span className="d-block mb-1">Date Range</span>
                <select
                  id="picking-date-range"
                  className="form-select form-select-sm"
                >
                  <option>Today</option>
                  <option>This Week</option>
                  <option>This Month</option>
                </select>
              </label>
            </div>

            {/* Picker */}
            <div>
              <label
                className="fz-12 text-muted d-block"
                htmlFor="picking-picker"
              >
                <span className="d-block mb-1">Picker</span>
                <select
                  id="picking-picker"
                  className="form-select form-select-sm"
                  value={selectedPickerId}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                    setSelectedPickerId(e.target.value);
                  }}
                >
                  {pickerStats.map((p) => (
                    <option
                      key={p.pickerUserId}
                      value={String(p.pickerUserId)}
                    >
                      {getPickerDisplayName(
                        p.pickerName,
                        p.pickerFirstName,
                        p.pickerLastName,
                      )}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {/* Route */}
            <div>
              <label
                className="fz-12 text-muted d-block"
                htmlFor="picking-route"
              >
                <span className="d-block mb-1">Route</span>
                <select
                  id="picking-route"
                  className="form-select form-select-sm"
                >
                  <option>All Routes</option>
                  {routes.map((r) => (
                    <option key={r.pickingId} value={r.pickingId}>
                      {r.routeName || `Picking #${r.pickingId}`}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>
        </div>

        {/* Top content: current picking + shorted items */}
        <div className="row">
          {/* Left column: current picking & routes */}
          <div className="col-xl-7 mb-4">
            {/* Current picking card */}
            <div className="card mb-3">
              <div className="card-body">
                <div className="d-flex justify-content-between mb-3">
                  <div>
                    <div className="fz-16 fw-600 mb-1">Current Picking</div>
                    <div className="fz-12 text-muted">
                      Who is currently picking and their progress.
                    </div>
                  </div>
                </div>

                {!currentPicking && (
                  <div className="alert alert-info mb-0">
                    No picker is currently picking a route.
                  </div>
                )}

                {currentPicking && (
                  <>
                    <div className="d-flex align-items-center mb-3">
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center me-3"
                        style={{
                          width: 48,
                          height: 48,
                          backgroundColor: '#e2e8f0',
                          fontWeight: 600,
                        }}
                      >
                        {getInitials(
                          getPickerDisplayName(
                            null,
                            currentPicking.pickerFirstName,
                            currentPicking.pickerLastName,
                          ),
                        )}
                      </div>
                      <div>
                        <div className="fw-600">
                          {getPickerDisplayName(
                            null,
                            currentPicking.pickerFirstName,
                            currentPicking.pickerLastName,
                          )}
                        </div>
                        <div className="fz-12 text-muted">
                          <span className="fw-600">
                            {currentPicking.routeName || ''}
                          </span>
                        </div>
                        <div className="fz-12 text-muted">
                          {`Started at ${
                            currentPicking.startPickingTime || '-'
                          } · Elapsed ${currentPicking.elapsedMinutes ?? 0} min`}
                        </div>
                      </div>
                    </div>

                    <div>
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <div className="fz-12 text-muted">
                          {`${itemsPicked} / ${totalItems} items`}
                        </div>
                        <div className="fz-12 text-muted">
                          {`${progressPct.toFixed(0)}%`}
                        </div>
                      </div>
                      <div className="progress" style={{ height: 8 }}>
                        <div
                          className="progress-bar bg-primary"
                          role="progressbar"
                          aria-label="Current picking progress"
                          style={{ width: `${progressPct}%` }}
                          aria-valuemin={0}
                          aria-valuemax={100}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Active routes card */}
            <div className="card">
              <div className="card-body">
                <div className="d-flex justify-content-between mb-2">
                  <div className="fz-16 fw-600">Active Routes</div>
                </div>
                <div className="table-responsive">
                  <table className="table table-sm mb-0 fz-13">
                    <thead>
                      <tr className="text-muted">
                        <th>Route</th>
                        <th>Driver</th>
                        <th>Status</th>
                        <th className="text-end">Vending Machines</th>
                      </tr>
                    </thead>
                    <tbody>
                      {routes.map((r) => (
                        <tr key={r.pickingId}>
                          <td>
                            <div className="fw-500">
                              {r.routeName || `Picking #${r.pickingId}`}
                            </div>
                          </td>
                          <td>
                            {getDriverDisplayName(
                              null,
                              r.driverFirstName,
                              r.driverLastName,
                            )}
                          </td>
                          <td>
                            <span className={getStatusBadgeClasses(r.status)}>
                              {getStatusLabel(r.status)}
                            </span>
                          </td>
                          <td className="text-end">
                            {`${r.pickedVM ?? 0} / ${r.notPickedVM ?? 0}`}
                          </td>
                        </tr>
                      ))}
                      {routes.length === 0 && (
                        <tr>
                          <td
                            colSpan={4}
                            className="text-center text-muted py-3"
                          >
                            No active routes.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
          {/* <-- added: close col-xl-7 */}

          {/* Right column: shorted items */}
          <div className="col-xl-5 mb-4">
            <div className="card h-100">
              <div className="card-body d-flex flex-column">
                <div className="d-flex justify-content-between mb-2">
                  <div>
                    <div className="fz-16 fw-600">Shorted Items</div>
                    <div className="fz-12 text-muted">
                      Items that could not be picked as requested.
                    </div>
                  </div>
                </div>

                <div className="table-responsive">
                  <table className="table table-sm mb-0 fz-13">
                    <thead>
                      <tr className="text-muted">
                        <th>Item</th>
                        <th>Route</th>
                        <th>Machine</th>
                        <th className="text-end">Qty Shorted</th>
                      </tr>
                    </thead>
                    <tbody>
                      {shortedItems.map((item) => {
                        const qtyShorted = item.qtyShorted
                          ?? Math.max(
                            item.quantity - (item.pickQuantity ?? 0),
                            0,
                          );
                        return (
                          <tr key={item.pickingItemId}>
                            <td>{item.productName}</td>
                            <td>{item.routeName}</td>
                            <td>{item.deviceId}</td>
                            <td className="text-end">{qtyShorted}</td>
                          </tr>
                        );
                      })}
                      {shortedItems.length === 0 && (
                        <tr>
                          <td
                            colSpan={4}
                            className="text-center text-muted py-3"
                          >
                            No shorted items.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom: picker performance */}
        <div className="row">
          <div className="col-xl-7 mb-4">
            <div className="card">
              <div className="card-body">
                <div className="d-flex justify-content-between mb-2">
                  <div className="fz-16 fw-600">Picker Performance (Today)</div>
                </div>
                <div className="table-responsive">
                  <table className="table table-sm mb-0 fz-13">
                    <thead>
                      <tr className="text-muted">
                        <th>Picker</th>
                        <th className="text-end">Time Worked</th>
                        <th className="text-end">Items Picked</th>
                        <th className="text-end">Items / Hour</th>
                        <th className="text-end">Routes Done</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pickerStats.map((p) => {
                        const secondsWorked = p.secondsWorked ?? 0;
                        const minutesWorked = Math.round(secondsWorked / 60);
                        const hours = secondsWorked / 3600;
                        const iph = hours > 0 && p.itemsPicked
                          ? p.itemsPicked / hours
                          : 0;
                        const displayName = getPickerDisplayName(
                          p.pickerName,
                          p.pickerFirstName,
                          p.pickerLastName,
                        );

                        return (
                          <tr key={p.pickerUserId}>
                            <td>{displayName}</td>
                            <td className="text-end">
                              {formatMinutesToHHMM(minutesWorked)}
                            </td>
                            <td className="text-end">
                              {p.itemsPicked ?? 0}
                            </td>
                            <td className="text-end">
                              {iph.toFixed(1)}
                            </td>
                            <td className="text-end">
                              {p.routesCompleted ?? 0}
                            </td>
                          </tr>
                        );
                      })}
                      {pickerStats.length === 0 && (
                        <tr>
                          <td
                            colSpan={5}
                            className="text-center text-muted py-3"
                          >
                            No picker performance data for today.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* Right column reserved for future widgets */}
          <div className="col-xl-5 mb-4" />
        </div>
      </div>
    </div>
  );
}

export default VendingPickingDashboard;
